import { useState, useEffect, useRef } from 'react'
import { useFriendNote } from '../hooks/useFriendNote'

/**
 * Friend Note Editor Component
 *
 * A rich textarea editor for private notes about friends.
 * Features:
 * - Auto-save with debouncing (1 second after typing stops)
 * - Character limit (1000 chars) with visual indicator
 * - Real-time sync across sessions
 * - Keyboard shortcuts (Escape to close, Ctrl/Cmd+S to save now)
 * - Privacy reminder tooltip
 * - Clear note button
 * - Save status indicators
 *
 * @param {string} userId - Current user's ID (note author)
 * @param {string} friendId - Friend's ID (note subject)
 * @param {string} friendUsername - Friend's username (for placeholder text)
 */
export default function FriendNoteEditor({ userId, friendId, friendUsername }) {
  const {
    note,
    loading,
    saving,
    error,
    characterCount,
    isAtLimit,
    updateNote,
    deleteNote,
    saveNow
  } = useFriendNote(userId, friendId)

  const [expanded, setExpanded] = useState(false)
  const [showSavedIndicator, setShowSavedIndicator] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const textareaRef = useRef(null)

  // Show "Saved ✓" indicator briefly after saving completes
  useEffect(() => {
    if (!saving && !error && note.length > 0) {
      setShowSavedIndicator(true)
      const timer = setTimeout(() => setShowSavedIndicator(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [saving, error, note])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape to collapse
      if (e.key === 'Escape' && expanded) {
        setExpanded(false)
        e.preventDefault()
      }

      // Ctrl/Cmd + S to force save
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && expanded) {
        saveNow()
        e.preventDefault()
      }
    }

    if (expanded) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [expanded, saveNow])

  // Auto-focus textarea when expanded
  useEffect(() => {
    if (expanded && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [expanded])

  const handleClearNote = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      setTimeout(() => setConfirmDelete(false), 3000)
      return
    }

    await deleteNote()
    setConfirmDelete(false)
    setExpanded(false)
  }

  const handleChange = (e) => {
    updateNote(e.target.value)
  }

  // Get character count color based on usage
  const getCharCountColor = () => {
    if (isAtLimit) return 'text-gjyl-danger'
    if (characterCount > 900) return 'text-gjyl-warning'
    return 'text-slate-400'
  }

  return (
    <div className="mt-6 p-4 glass rounded-gjyl transition-all">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gjyl-indigo-400 uppercase tracking-wide">
          📝 Private Note
          <span className="ml-2 text-xs font-normal text-slate-400 cursor-help" title="This note is completely private. Only you can see it.">
            (Only you can see this)
          </span>
        </h3>

        {!loading && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-gjyl-indigo-400 hover:text-gjyl-indigo-300 transition-colors"
            aria-label={expanded ? 'Collapse note editor' : 'Expand note editor'}
          >
            {expanded ? '▼ Collapse' : '▶ Expand'}
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gjyl-indigo-500"></div>
        </div>
      )}

      {/* Editor */}
      {!loading && expanded && (
        <div className="space-y-3 animate-fade-in">
          {/* Textarea */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={note}
              onChange={handleChange}
              placeholder={`Add a private note about ${friendUsername}...`}
              className={`w-full min-h-[120px] p-3 bg-gjyl-slate-900/50 rounded-gjyl text-white placeholder-slate-500
                         border ${error ? 'border-gjyl-danger' : 'border-gjyl-slate-700/50'}
                         focus:border-gjyl-indigo-500 focus:ring-2 focus:ring-gjyl-indigo-500/30
                         outline-none resize-y transition-all
                         ${isAtLimit ? 'border-gjyl-danger focus:border-gjyl-danger focus:ring-gjyl-danger/30' : ''}`}
              maxLength={1000}
            />

            {/* Character Counter */}
            <div className={`absolute bottom-2 right-2 text-xs font-mono ${getCharCountColor()}`}>
              {characterCount}/1000
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-3 py-2 bg-gjyl-danger/10 border border-gjyl-danger/30 rounded-gjyl text-sm text-gjyl-danger animate-pulse">
              ⚠️ {error}
            </div>
          )}

          {/* Actions Row */}
          <div className="flex items-center justify-between">
            {/* Left: Status Indicators */}
            <div className="flex items-center gap-3">
              {/* Saving Indicator */}
              {saving && (
                <div className="flex items-center gap-2 text-sm text-gjyl-indigo-400 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-gjyl-indigo-400 animate-ping"></div>
                  Saving...
                </div>
              )}

              {/* Saved Indicator */}
              {showSavedIndicator && !saving && (
                <div className="flex items-center gap-2 text-sm text-gjyl-success animate-fade-in">
                  ✓ Saved
                </div>
              )}

              {/* Keyboard Shortcuts Hint */}
              {!saving && !showSavedIndicator && (
                <div className="text-xs text-slate-500">
                  <kbd className="px-1.5 py-0.5 bg-gjyl-slate-800 rounded border border-gjyl-slate-700">Esc</kbd> to close
                  {' • '}
                  <kbd className="px-1.5 py-0.5 bg-gjyl-slate-800 rounded border border-gjyl-slate-700">Ctrl+S</kbd> to save now
                </div>
              )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Clear Note Button */}
              {note.length > 0 && (
                <button
                  onClick={handleClearNote}
                  className={`px-3 py-1.5 text-sm font-medium rounded-gjyl transition-all hover:scale-105 active:scale-95
                           ${confirmDelete
                             ? 'bg-gjyl-danger/20 border border-gjyl-danger text-gjyl-danger animate-pulse'
                             : 'bg-gjyl-slate-800/50 border border-gjyl-slate-700/50 text-slate-400 hover:text-white hover:border-gjyl-slate-600'
                           }`}
                  title={confirmDelete ? 'Click again to confirm deletion' : 'Clear this note'}
                >
                  {confirmDelete ? '⚠️ Confirm Delete?' : '🗑️ Clear'}
                </button>
              )}

              {/* Force Save Button */}
              <button
                onClick={saveNow}
                disabled={saving}
                className="px-3 py-1.5 bg-gjyl-indigo-500/20 border border-gjyl-indigo-500/50 text-gjyl-indigo-400
                         hover:bg-gjyl-indigo-500/30 hover:border-gjyl-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm font-medium rounded-gjyl transition-all hover:scale-105 active:scale-95"
                title="Save note immediately"
              >
                💾 Save Now
              </button>
            </div>
          </div>

          {/* Note Preview (when collapsed and note exists) */}
          {note.length > 0 && (
            <div className="text-xs text-slate-500 italic">
              Auto-saves 1 second after you stop typing
            </div>
          )}
        </div>
      )}

      {/* Collapsed Preview */}
      {!loading && !expanded && note.length > 0 && (
        <div
          onClick={() => setExpanded(true)}
          className="p-3 bg-gjyl-slate-900/30 rounded-gjyl border border-gjyl-slate-700/30 cursor-pointer
                   hover:bg-gjyl-slate-900/50 hover:border-gjyl-slate-600/50 transition-all group"
        >
          <p className="text-sm text-slate-400 line-clamp-2 group-hover:text-slate-300 transition-colors">
            {note}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{characterCount} characters</span>
            <span className="text-gjyl-indigo-400 group-hover:text-gjyl-indigo-300">Click to edit →</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !expanded && note.length === 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full p-3 bg-gjyl-slate-900/20 border border-dashed border-gjyl-slate-700/50 rounded-gjyl
                   hover:bg-gjyl-slate-900/40 hover:border-gjyl-indigo-500/50 transition-all group"
        >
          <p className="text-sm text-slate-500 group-hover:text-gjyl-indigo-400 transition-colors">
            ✍️ Click to add a note about {friendUsername}
          </p>
        </button>
      )}
    </div>
  )
}
